import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon,
  TagIcon,
  DocumentTextIcon,
  CalculatorIcon,
  CalendarIcon,
  ListBulletIcon,
  CheckIcon,
  AdjustmentsVerticalIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { cleanDataForAPI } from '../../utils/data-cleanup';

interface ItemAttribute {
  id: string;
  code: string;
  name: string;
  description?: string;
  dataType: string;
  groupName?: string;
  defaultValue?: string;
  listValues?: string[];
  minValue?: number;
  maxValue?: number;
  isRequired: boolean;
  isSearchable: boolean;
  isActive: boolean;
  sortOrder?: number;
  isUnique: boolean;
  isVariant: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  createdAt: string;
  updatedAt: string;
}

const AttributeDataType = {
  TEXT: 'text',
  NUMBER: 'number',
  DECIMAL: 'decimal',
  DATE: 'date',
  BOOLEAN: 'boolean',
  LIST: 'list',
  MULTI_SELECT: 'multi_select',
};

const AttributeCategory = {
  PHYSICAL: 'physical',
  CHEMICAL: 'chemical',
  QUALITY: 'quality',
  SPECIFICATION: 'specification',
  CLASSIFICATION: 'classification',
  CUSTOM: 'custom',
};

const ItemAttributeManagement: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAttribute, setSelectedAttribute] = useState<ItemAttribute | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterDataType, setFilterDataType] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'grouped-category' | 'grouped-data-type'>('grid');
  
  const queryClient = useQueryClient();

  // Fetch item attributes
  const { data: attributesData, isLoading } = useQuery({
    queryKey: ['itemAttributes', searchTerm, filterCategory, filterDataType],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterCategory) params.append('groupName', filterCategory);
      if (filterDataType) params.append('dataType', filterDataType);
      
      const response = await api.get(`/master-data/item-attributes?${params}`);
      return response.data;
    },
  });

  // Fetch grouped data by category
  const { data: groupedByCategory } = useQuery({
    queryKey: ['itemAttributesGroupedCategory'],
    queryFn: async () => {
      const response = await api.get('/master-data/item-attributes/grouped/group');
      return response.data;
    },
    enabled: viewMode === 'grouped-category',
  });

  // Fetch grouped data by data type
  const { data: groupedByDataType } = useQuery({
    queryKey: ['itemAttributesGroupedDataType'],
    queryFn: async () => {
      const response = await api.get('/master-data/item-attributes/grouped/data-type');
      return response.data;
    },
    enabled: viewMode === 'grouped-data-type',
  });

  // Create/Update attribute
  const createUpdateMutation = useMutation({
    mutationFn: async (data: any) => {
      // Clean the data and specifically remove helpText which is only for UI
      const cleanData = cleanDataForAPI(data);
      delete cleanData.helpText; // Remove helpText as it's not a backend field
      
      if (selectedAttribute) {
        return api.patch(`/master-data/item-attributes/${selectedAttribute.id}`, cleanData);
      }
      return api.post('/master-data/item-attributes', cleanData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['itemAttributes'] });
      queryClient.invalidateQueries({ queryKey: ['itemAttributesGroupedCategory'] });
      queryClient.invalidateQueries({ queryKey: ['itemAttributesGroupedDataType'] });
      toast.success(selectedAttribute ? 'Attribute updated successfully' : 'Attribute created successfully');
      closeModal();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Operation failed');
    },
  });

  // Delete attribute
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/master-data/item-attributes/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['itemAttributes'] });
      queryClient.invalidateQueries({ queryKey: ['itemAttributesGroupedCategory'] });
      queryClient.invalidateQueries({ queryKey: ['itemAttributesGroupedDataType'] });
      toast.success('Attribute deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Delete failed');
    },
  });

  // Toggle status
  const toggleStatusMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.patch(`/master-data/item-attributes/${id}/toggle-status`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['itemAttributes'] });
      queryClient.invalidateQueries({ queryKey: ['itemAttributesGroupedCategory'] });
      queryClient.invalidateQueries({ queryKey: ['itemAttributesGroupedDataType'] });
      toast.success('Status updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Status update failed');
    },
  });

  const openModal = (attribute?: ItemAttribute) => {
    setSelectedAttribute(attribute || null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedAttribute(null);
  };

  const getDataTypeIcon = (dataType: string) => {
    switch (dataType) {
      case AttributeDataType.TEXT:
        return <DocumentTextIcon className="h-5 w-5" />;
      case AttributeDataType.NUMBER:
      case AttributeDataType.DECIMAL:
        return <CalculatorIcon className="h-5 w-5" />;
      case AttributeDataType.DATE:
        return <CalendarIcon className="h-5 w-5" />;
      case AttributeDataType.BOOLEAN:
        return <CheckIcon className="h-5 w-5" />;
      case AttributeDataType.LIST:
      case AttributeDataType.MULTI_SELECT:
        return <ListBulletIcon className="h-5 w-5" />;
      default:
        return <TagIcon className="h-5 w-5" />;
    }
  };

  const getDataTypeColor = (dataType: string) => {
    const dataTypeColors = {
      [AttributeDataType.TEXT]: 'bg-blue-100 text-blue-800',
      [AttributeDataType.NUMBER]: 'bg-green-100 text-green-800',
      [AttributeDataType.DECIMAL]: 'bg-green-100 text-green-800',
      [AttributeDataType.DATE]: 'bg-purple-100 text-purple-800',
      [AttributeDataType.BOOLEAN]: 'bg-yellow-100 text-yellow-800',
      [AttributeDataType.LIST]: 'bg-indigo-100 text-indigo-800',
      [AttributeDataType.MULTI_SELECT]: 'bg-red-100 text-red-800',
    };

    return dataTypeColors[dataType] || 'bg-gray-100 text-gray-800';
  };

  const getGroupColor = (groupName?: string) => {
    if (!groupName) return 'bg-gray-50 text-gray-700';
    
    const groupColors: Record<string, string> = {
      'Physical': 'bg-blue-50 text-blue-700',
      'Chemical': 'bg-green-50 text-green-700',
      'Quality': 'bg-purple-50 text-purple-700',
      'Specification': 'bg-orange-50 text-orange-700',
      'Classification': 'bg-indigo-50 text-indigo-700',
      'Custom': 'bg-gray-50 text-gray-700',
    };

    return groupColors[groupName] || 'bg-gray-50 text-gray-700';
  };

  const renderAttributeCard = (attribute: ItemAttribute) => (
    <Card key={attribute.id} className="hover:shadow-lg transition-shadow">
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            {getDataTypeIcon(attribute.dataType)}
            <div>
              <h3 className="font-semibold text-gray-900">{attribute.name}</h3>
              <p className="text-sm text-gray-500">{attribute.code}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getDataTypeColor(attribute.dataType)}`}>
              {attribute.dataType}
            </span>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Category:</span>
            <span className={`px-2 py-0.5 text-xs rounded ${getGroupColor(attribute.groupName)}`}>
              {attribute.groupName || 'No Group'}
            </span>
          </div>
          
          
          {attribute.defaultValue && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Default Value:</span>
              <span className="font-medium">{attribute.defaultValue}</span>
            </div>
          )}
          
          {attribute.description && (
            <div className="text-sm">
              <span className="text-gray-600">Description:</span>
              <p className="text-gray-800 mt-1">{attribute.description}</p>
            </div>
          )}
          
          <div className="flex flex-wrap gap-1 mt-2">
            {attribute.isRequired && (
              <span className="px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded">
                Mandatory
              </span>
            )}
            {attribute.isSearchable && (
              <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">
                Searchable
              </span>
            )}
            {attribute.listValues && attribute.listValues.length > 0 && (
              <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded">
                Has Options ({attribute.listValues.length})
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t">
          <button
            onClick={() => toggleStatusMutation.mutate(attribute.id)}
            className={`flex items-center gap-1 px-2 py-1 rounded text-sm ${
              attribute.isActive
                ? 'text-green-600 hover:bg-green-50'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {attribute.isActive ? (
              <CheckCircleIcon className="h-4 w-4" />
            ) : (
              <XCircleIcon className="h-4 w-4" />
            )}
            {attribute.isActive ? 'Active' : 'Inactive'}
          </button>

          <div className="flex gap-2">
            <button
              onClick={() => openModal(attribute)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <PencilIcon className="h-4 w-4 text-gray-600" />
            </button>
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to delete this attribute?')) {
                  deleteMutation.mutate(attribute.id);
                }
              }}
              className="p-1 hover:bg-red-50 rounded"
            >
              <TrashIcon className="h-4 w-4 text-red-600" />
            </button>
          </div>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Item Attribute Management</h1>
        <p className="text-gray-600">Configure item attributes and their properties</p>
      </div>

      {/* Filters and Actions */}
      <Card className="mb-6">
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search attributes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Categories</option>
              {Object.entries(AttributeCategory).map(([key, value]) => (
                <option key={value} value={value}>
                  {key.charAt(0) + key.slice(1).toLowerCase()}
                </option>
              ))}
            </select>

            <select
              value={filterDataType}
              onChange={(e) => setFilterDataType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Data Types</option>
              {Object.entries(AttributeDataType).map(([key, value]) => (
                <option key={value} value={value}>
                  {key.charAt(0) + key.slice(1).toLowerCase()}
                </option>
              ))}
            </select>

            <div className="flex gap-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-2 rounded-lg text-sm ${
                  viewMode === 'grid'
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                Grid
              </button>
              <button
                onClick={() => setViewMode('grouped-category')}
                className={`px-3 py-2 rounded-lg text-sm ${
                  viewMode === 'grouped-category'
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                Category
              </button>
              <button
                onClick={() => setViewMode('grouped-data-type')}
                className={`px-3 py-2 rounded-lg text-sm ${
                  viewMode === 'grouped-data-type'
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                Type
              </button>
            </div>

            <div></div>
            <div></div>

            <Button onClick={() => openModal()} className="flex items-center gap-2">
              <PlusIcon className="h-5 w-5" />
              Add Attribute
            </Button>
          </div>
        </div>
      </Card>

      {/* Content based on view mode */}
      {isLoading ? (
        <div className="text-center py-8">Loading...</div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {attributesData?.data?.map((attribute: ItemAttribute) => renderAttributeCard(attribute))}
        </div>
      ) : viewMode === 'grouped-category' ? (
        <div className="space-y-6">
          {Object.entries(AttributeCategory).map(([categoryKey, categoryValue]) => (
            <Card key={categoryValue}>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-4">
                  <AdjustmentsVerticalIcon className="h-5 w-5" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    {categoryKey.charAt(0) + categoryKey.slice(1).toLowerCase()}
                  </h3>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getGroupColor(categoryValue)}`}>
                    {groupedByCategory?.[categoryValue]?.length || 0} attributes
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedByCategory?.[categoryValue]?.map((attribute: ItemAttribute) => renderAttributeCard(attribute))}
                </div>
                {(!groupedByCategory?.[categoryValue] || groupedByCategory[categoryValue].length === 0) && (
                  <p className="text-gray-500 text-center py-4">No attributes found for this category</p>
                )}
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(AttributeDataType).map(([dataTypeKey, dataTypeValue]) => (
            <Card key={dataTypeValue}>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-4">
                  {getDataTypeIcon(dataTypeValue)}
                  <h3 className="text-lg font-semibold text-gray-900">
                    {dataTypeKey.charAt(0) + dataTypeKey.slice(1).toLowerCase()}
                  </h3>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getDataTypeColor(dataTypeValue)}`}>
                    {groupedByDataType?.[dataTypeValue]?.length || 0} attributes
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedByDataType?.[dataTypeValue]?.map((attribute: ItemAttribute) => renderAttributeCard(attribute))}
                </div>
                {(!groupedByDataType?.[dataTypeValue] || groupedByDataType[dataTypeValue].length === 0) && (
                  <p className="text-gray-500 text-center py-4">No attributes found for this data type</p>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <AttributeModal
          attribute={selectedAttribute}
          onClose={closeModal}
          onSubmit={(data) => createUpdateMutation.mutate(data)}
        />
      )}
    </div>
  );
};

// Attribute Modal Component
const AttributeModal: React.FC<{
  attribute: ItemAttribute | null;
  onClose: () => void;
  onSubmit: (data: any) => void;
}> = ({ attribute, onClose, onSubmit }) => {
  const { register, handleSubmit, watch, formState: { errors } } = useForm<any>({
    defaultValues: attribute || {
      dataType: AttributeDataType.TEXT,
      groupName: '',
      isRequired: false,
      isSearchable: true,
      isActive: true,
      sortOrder: 0,
    },
  });

  const watchedDataType = watch('dataType');

  const handleFormSubmit = (data: any) => {
    // Clean up data
    const cleanData = { ...data };
    
    // Convert listValues string to array if needed
    if (cleanData.listValues && typeof cleanData.listValues === 'string') {
      cleanData.listValues = cleanData.listValues
        .split(',')
        .map((item: string) => item.trim())
        .filter((item: string) => item.length > 0);
    }

    // Remove fields that are not relevant for certain data types
    if (cleanData.dataType !== AttributeDataType.LIST && cleanData.dataType !== AttributeDataType.MULTI_SELECT) {
      delete cleanData.listValues;
    }

    if (cleanData.dataType !== AttributeDataType.NUMBER && cleanData.dataType !== AttributeDataType.DECIMAL) {
      delete cleanData.minValue;
      delete cleanData.maxValue;
    }

    onSubmit(cleanData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">
          {attribute ? 'Edit Attribute' : 'Add New Attribute'}
        </h2>
        
        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Code *
              </label>
              <input
                {...register('code', { required: 'Code is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              {errors.code && (
                <p className="text-red-500 text-sm mt-1">{errors.code.message as string}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <input
                {...register('name', { required: 'Name is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name.message as string}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data Type *
              </label>
              <select
                {...register('dataType', { required: 'Data type is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {Object.entries(AttributeDataType).map(([key, value]) => (
                  <option key={value} value={value}>
                    {key.charAt(0) + key.slice(1).toLowerCase()}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category *
              </label>
              <select
                {...register('groupName')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {Object.entries(AttributeCategory).map(([key, value]) => (
                  <option key={value} value={value}>
                    {key.charAt(0) + key.slice(1).toLowerCase()}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unit
              </label>
              <input
                {...register('pattern')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., mm, kg, %"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Value
              </label>
              <input
                {...register('defaultValue')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              {...register('description')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {(watchedDataType === AttributeDataType.LIST || watchedDataType === AttributeDataType.MULTI_SELECT) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Possible Values (comma-separated)
              </label>
              <textarea
                {...register('listValues')}
                rows={3}
                placeholder="Small, Medium, Large, Extra Large"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          )}

          {(watchedDataType === AttributeDataType.NUMBER || watchedDataType === AttributeDataType.DECIMAL) && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Minimum Value
                </label>
                <input
                  type="number"
                  {...register('minValue')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Maximum Value
                </label>
                <input
                  type="number"
                  {...register('maxValue')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Help Text
            </label>
            <textarea
              {...register('helpText')}
              rows={2}
              placeholder="Instructions to help users understand this attribute"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Display Order
              </label>
              <input
                type="number"
                min="1"
                {...register('sortOrder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div></div>
          </div>

          <div className="space-y-3">
            <h3 className="text-lg font-medium text-gray-900">Settings</h3>
            <div className="grid grid-cols-3 gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  {...register('isRequired')}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Mandatory</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  {...register('isSearchable')}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Searchable</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  {...register('isActive')}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Active</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              {attribute ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ItemAttributeManagement;